import React from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from "reactstrap";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";

// Constants for common configurations with translation keys
export const MODAL_VARIANTS = {
  DELETE: {
    titleKey: "confirmModal.variants.delete.title",
    messageKey: "confirmModal.variants.delete.message",
    confirmLabelKey: "confirmModal.variants.delete.confirmLabel",
    confirmColor: "danger",
    icon: "🗑️",
  },
  SAVE: {
    titleKey: "confirmModal.variants.save.title",
    messageKey: "confirmModal.variants.save.message",
    confirmLabelKey: "confirmModal.variants.save.confirmLabel",
    confirmColor: "primary",
    icon: "💾",
  },
  LOGOUT: {
    titleKey: "confirmModal.variants.logout.title",
    messageKey: "confirmModal.variants.logout.message",
    confirmLabelKey: "confirmModal.variants.logout.confirmLabel",
    confirmColor: "warning",
    icon: "🚪",
  },
  DISCARD: {
    titleKey: "confirmModal.variants.discard.title",
    messageKey: "confirmModal.variants.discard.message",
    confirmLabelKey: "confirmModal.variants.discard.confirmLabel",
    confirmColor: "danger",
    icon: "⚠️",
  },
  SUBMIT: {
    titleKey: "confirmModal.variants.submit.title",
    messageKey: "confirmModal.variants.submit.message",
    confirmLabelKey: "confirmModal.variants.submit.confirmLabel",
    confirmColor: "success",
    icon: "✅",
  },
};

export const MODAL_SIZES = {
  SMALL: "sm",
  MEDIUM: "md",
  LARGE: "lg",
  EXTRA_LARGE: "xl",
};

const ConfirmModal = ({
  isOpen,
  variant,
  title,
  message,
  confirmLabel,
  cancelLabel,
  confirmColor,
  cancelColor = "secondary",
  size = MODAL_SIZES.SMALL,
  centered = true,
  backdrop = true,
  keyboard = true,
  fade = true,
  autoFocus = true,
  showIcon = false,
  icon,
  loading = false,
  disabled = false,
  customStyle = {},
  headerClassName = "",
  bodyClassName = "",
  footerClassName = "",
  confirmButtonProps = {},
  cancelButtonProps = {},
  toggle,
  onConfirm,
  onCancel,
  children,
  t, // Translation function
}) => {
  // Use variant defaults if provided, otherwise use individual props
  const config = variant ? MODAL_VARIANTS[variant] : {};

  // Get translated text using translation keys
  const finalTitle =
    title ||
    (config.titleKey ? t(config.titleKey) : t("confirmModal.defaults.title"));
  const finalMessage =
    message ||
    (config.messageKey
      ? t(config.messageKey)
      : t("confirmModal.defaults.message"));
  const finalConfirmLabel =
    confirmLabel ||
    (config.confirmLabelKey
      ? t(config.confirmLabelKey)
      : t("confirmModal.defaults.confirmLabel"));
  const finalCancelLabel =
    cancelLabel || t("confirmModal.defaults.cancelLabel");
  const finalConfirmColor = confirmColor || config.confirmColor || "primary";
  const finalIcon = icon || config.icon;

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      toggle();
    }
  };

  const handleConfirm = async () => {
    if (onConfirm) {
      await onConfirm();
    }
  };

  return (
    <Modal
      size={size}
      isOpen={isOpen}
      toggle={toggle}
      centered={centered}
      backdrop={backdrop}
      keyboard={keyboard}
      fade={fade}
      autoFocus={autoFocus}
      style={customStyle}
    >
      <ModalHeader toggle={toggle} className={headerClassName}>
        {showIcon && finalIcon && (
          <span className="me-2" style={{ fontSize: "1.2em" }}>
            {finalIcon}
          </span>
        )}
        {finalTitle}
      </ModalHeader>

      <ModalBody className={bodyClassName}>
        {children || finalMessage}
      </ModalBody>

      <ModalFooter className={footerClassName}>
        <Button
          color={cancelColor}
          onClick={handleCancel}
          disabled={loading}
          {...cancelButtonProps}
        >
          {finalCancelLabel}
        </Button>

        <Button
          color={finalConfirmColor}
          onClick={handleConfirm}
          disabled={disabled || loading}
          {...confirmButtonProps}
        >
          {loading && (
            <span
              className="spinner-border spinner-border-sm me-2"
              role="status"
              aria-hidden="true"
            />
          )}
          {finalConfirmLabel}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

ConfirmModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  variant: PropTypes.oneOf(Object.keys(MODAL_VARIANTS)),
  title: PropTypes.string,
  message: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  confirmLabel: PropTypes.string,
  cancelLabel: PropTypes.string,
  confirmColor: PropTypes.string,
  cancelColor: PropTypes.string,
  size: PropTypes.oneOf(Object.values(MODAL_SIZES)),
  centered: PropTypes.bool,
  backdrop: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  keyboard: PropTypes.bool,
  fade: PropTypes.bool,
  autoFocus: PropTypes.bool,
  showIcon: PropTypes.bool,
  icon: PropTypes.string,
  loading: PropTypes.bool,
  disabled: PropTypes.bool,
  customStyle: PropTypes.object,
  headerClassName: PropTypes.string,
  bodyClassName: PropTypes.string,
  footerClassName: PropTypes.string,
  confirmButtonProps: PropTypes.object,
  cancelButtonProps: PropTypes.object,
  onConfirm: PropTypes.func,
  onCancel: PropTypes.func,
  children: PropTypes.node,
  t: PropTypes.func.isRequired,
};

export default withTranslation()(ConfirmModal);
